// Grupos de mesas en la escena (04-11, «mesas de equipo», web/qa/auditoria-apple/propuesta-2/fuentes/grupos.md §4): lo
// PURO que necesita la escena para crear y ofrecer grupos. El nombre que se sugiere al crear uno («Grupo nuevo…», Ctrl+G)
// y los destinos de «Mover a ▸».
import { t } from '../../../i18n';
import type { ContratadoDTO, GrupoDTO } from '../compartido/vocabulario';
import { YO } from '../pelotas/tiempo';
import type { ContratoDTO } from '../pelotas/tipos';
import type { DestinoGrupo } from './MenuAgente';

/** Sin mayúsculas ni tildes (como el buscador, marco/Saltar.tsx). */
const normalizar = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** Palabras que no dan nombre a un grupo. */
const VACIAS = new Set(['para', 'como', 'desde', 'hasta', 'sobre', 'entre', 'todos', 'todas', 'nuevo', 'nueva', 'nuevos', 'nuevas', 'cambiar', 'revisar', 'hacer']);
const MAXIMO = 24;

const capital = (s: string) => s.charAt(0).toLocaleUpperCase('es') + s.slice(1).toLocaleLowerCase('es');

/**
 * El nombre (y el contrato) que se propone al crear un grupo con estos agentes. Si alguno tiene un contrato con Santi
 * cuya pelota tiene Santi: «Esperando a Santi», atado a ese contrato (grupos.md §4). Si no, la palabra que más se repite
 * en sus nombres cortos («PRECIOS PACKS» + «PRECIOS CRM» → «Precios»); si no hay, «Grupo nuevo». Nunca uno que ya use
 * otro grupo con gente de la sala (se numera: «Precios 2»).
 */
export function sugerirGrupo(elegidos: readonly Pick<ContratadoDTO, 'agentId' | 'tema'>[], contratos: readonly ContratoDTO[] | null, enUso: readonly string[]): { nombre: string; esperaTemaId: number | null } {
  const ids = new Set(elegidos.map((c) => c.agentId));
  const contrato = contratos?.find((c) => c.agentId && ids.has(c.agentId) && c.lado !== YO);
  let base: string;
  if (contrato) base = t('oficinaV2.grupos.esperandoSanti');
  else {
    const cuenta = new Map<string, { n: number; palabra: string }>();
    for (const c of elegidos) {
      const palabras = new Set(c.tema.split(/[^\p{L}\p{N}]+/u).filter((p) => p.length >= 4 && !VACIAS.has(normalizar(p))));
      for (const p of palabras) {
        const k = normalizar(p);
        cuenta.set(k, { n: (cuenta.get(k)?.n ?? 0) + 1, palabra: cuenta.get(k)?.palabra ?? p });
      }
    }
    const minimo = Math.min(2, elegidos.length);
    const mejor = [...cuenta.values()].filter((x) => x.n >= minimo).sort((a, b) => b.n - a.n)[0];
    base = mejor ? capital(mejor.palabra).slice(0, MAXIMO) : t('oficinaV2.grupos.nombreNuevo');
  }
  const usados = new Set(enUso.map(normalizar));
  let nombre = base;
  for (let i = 2; usados.has(normalizar(nombre)); i += 1) nombre = `${base.slice(0, MAXIMO - String(i).length - 1)} ${i}`;
  return { nombre, esperaTemaId: contrato?.temaId ?? null };
}

/**
 * Los destinos de «Mover a ▸» en una sala: sus grupos con gente (en el orden de Pau, con cuántos tienen y si esperan a
 * Santi) y, detrás, los que se vaciaron hace menos de 7 días (sugerencias para series como «CALYPSO 7 → 7.1», regla 7).
 */
export function destinosDeSala(
  projectId: string,
  grupos: readonly GrupoDTO[],
  contratados: readonly Pick<ContratadoDTO, 'projectId' | 'grupoId'>[],
  esperaSanti: (grupoId: string) => boolean,
): DestinoGrupo[] {
  const n = (id: string) => contratados.filter((c) => c.grupoId === id).length;
  const deSala = grupos.filter((g) => g.projectId === projectId).sort((a, b) => a.orden - b.orden);
  const conGente = deSala.filter((g) => n(g.id) > 0);
  const vacios = deSala.filter((g) => n(g.id) === 0);
  return [...conGente, ...vacios].map((g) => ({ grupoId: g.id, nombre: g.nombre, n: n(g.id), conSanti: esperaSanti(g.id) }));
}
